/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// React
import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";

// react-router-dom components
import { Link, useNavigate, Navigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";

// Icons
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import NotificationsIcon from "@mui/icons-material/Notifications";
import EmailIcon from "@mui/icons-material/Email";
import ImageIcon from "@mui/icons-material/Image";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import BasicLayout from "examples/LayoutContainers/BasicLayout";

// Images
import bgImage from "assets/images/bg-sign-up-cover.jpeg";
import { useApi, useUser, useToken } from "../../../context/api.js";
import "react-image-crop/dist/ReactCrop.css";
import ReactCrop from "react-image-crop";
import Switch from "@mui/material/Switch";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";

const DEFAULT_CROP = { x: 0, y: 0, width: 192, height: 192, aspect: 1, newImage: true };

function SignUp({ className }) {
  const api = useApi();
  const token = useToken();
  const user = useUser();
  const navigate = useNavigate();

  // Data
  const [userData, setUserData] = useState({});
  const [step, setStep] = useState(1);

  // Step 1
  const [systemData, setSystemData] = useState({});
  const [loading, setLoading] = useState("Récupération des informations en cours...");

  // Step 2
  const [upImg, setUpImg] = useState();
  const imgRef = useRef(null);
  const [crop, setCrop] = useState(DEFAULT_CROP);

  // Step 3
  const [notifications, setNotifications] = useState(false);
  const [emails, setEmails] = useState(true);

  // Submit
  const [error, setError] = useState({ open: false });
  const closeError = () => setError((e) => ({ ...e, open: false }));

  useEffect(() => {
    if (user) {
      if (user.id) {
        navigate("/", { replace: true });
      } else {
        api
          .getSystemData()
          .then((data) => {
            setSystemData(data);
            setUserData((s) => ({ ...s, name: data.name, email: data.email }));
            setLoading(false);
          })
          .catch(() => setLoading(false));
      }
    }
  }, [user]);

  // No login
  if (!token) {
    return <Navigate to={"/login"} replace />;
  }

  // Step 1
  const handleChange = (e) => {
    if (e.target.name) {
      setUserData((s) => ({ ...s, [e.target.name]: e.target.value }));
    }
  };

  const handleSubmitStep1 = (e) => {
    e.preventDefault();

    if (userData.name) {
      setStep(2);
    }
  };

  const saveProfile = () => {
    const sendFile = () =>
      new Promise((accept) => {
        if (!crop || !imgRef.current) {
          accept(null);
        }

        const image = imgRef.current;

        const canvas = document.createElement("canvas");
        const scaleX = image.naturalWidth / crop.imageWidth;
        const scaleY = image.naturalHeight / crop.imageHeight;
        const ctx = canvas.getContext("2d");

        canvas.width = crop.width;
        canvas.height = crop.height;

        ctx.imageSmoothingQuality = "high";

        ctx.drawImage(
          image,
          crop.x * scaleX,
          crop.y * scaleY,
          crop.width * scaleX,
          crop.height * scaleY,
          0,
          0,
          crop.width,
          crop.height
        );

        // As a blob
        canvas.toBlob(
          (blob) => {
            // Upload picture
            api.uploadFile(blob, "profile", "image/png", "crop.png").then((res) => accept(res.id));
          },
          "image/png",
          1
        );
      });

    setLoading("Mise à jour du profil...");

    // Send profile file
    sendFile().then((pictureId) => {
      // TODO : Save notification subscription

      // Send profile data
      api
        .updateProfile({
          name: userData.name,
          email: userData.email,
          picture: pictureId,
          email_notifications: !!userData.email && emails ? "1" : "0",
        })
        .then((res) => {
          if (!res) {
            setError({ open: true });
            setLoading(false);
          } else {
            api.reloadProfile();
          }
        });
    });
  };

  // Step 2
  const onImageLoad = (image) => {
    imgRef.current = image;

    if (crop.newImage) {
      const crop = {
        aspect: 1,
        x: image.width / 2 - 64,
        y: image.height / 2 - 64,
        width: 128,
        height: 128,
        isVertical: image.height > image.width,
        imageWidth: image.width,
        imageHeight: image.height,
      };
      setCrop(crop);

      if (crop.isVertical) {
        // We need to update image size after resize (set to vertical image)
        setTimeout(() => setCropWithImageDimension(crop), 100);
      }
    } else {
      setCrop({ ...crop });
    }

    return false;
  };

  const setCropWithImageDimension = (crop) => {
    if (imgRef.current) {
      setCrop({
        ...crop,
        imageWidth: imgRef.current.width,
        imageHeight: imgRef.current.height,
      });
    }
  };

  const onSelectFile = (e) => {
    const uploadInput = e.target;
    if (uploadInput.files && uploadInput.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setUpImg(reader.result);
        uploadInput.value = "";
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const clearImage = () => {
    setUpImg(null);
    setCrop(DEFAULT_CROP);
    imgRef.current = null;
  };

  const handleBackStep2 = (e) => {
    setStep(1);
  };

  const handleSubmitStep2 = (e) => {
    setStep(3);
  };

  // Step 3
  const handleBackStep3 = () => {
    setStep(2);
  };

  const notificationsDisabled = true;

  return (
    <BasicLayout image={bgImage}>
      <Card className={className}>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="success"
          mx={2}
          mt={-3}
          p={3}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Bienvenue !
          </MDTypography>
          <MDTypography display="block" variant="button" color="white" my={1}>
            Nous allons maintenant configurer votre compte sur la plateforme de devoirs.
          </MDTypography>
        </MDBox>
        <MDBox pt={2} pb={3} px={3} style={{ position: "relative" }}>
          <MDBox pb={2}>
            <MDTypography variant="h6" fontWeight="medium" mt={1}>
              {step === 1 && "1/3. Informations personnelles"}
              {step === 2 && "2/3. Image de profil"}
              {step === 3 && "3/3. Alertes et notifications"}
            </MDTypography>
          </MDBox>
          {step === 1 && (
            <>
              <MDBox component="form" role="form" onSubmit={handleSubmitStep1}>
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="Nom"
                    name="name"
                    value={userData.name || ""}
                    onChange={handleChange}
                    error={!userData.name}
                    variant="standard"
                    fullWidth
                  />
                </MDBox>
                <MDBox mb={2}>
                  <MDInput
                    type="email"
                    label="Courriel"
                    name="email"
                    value={userData.email || ""}
                    onChange={handleChange}
                    disabled={!!systemData.email}
                    variant="standard"
                    fullWidth
                  />
                </MDBox>
                <MDBox mt={4} mb={1}>
                  <MDButton
                    variant="gradient"
                    color="info"
                    fullWidth
                    type="submit"
                    disabled={!userData.name}
                  >
                    Suivant <ArrowForwardIcon color={"inherit"} />
                  </MDButton>
                </MDBox>
                <MDBox mt={3} mb={1} textAlign="center">
                  <MDTypography variant="button" color="text">
                    <MDTypography
                      component={Link}
                      variant="button"
                      color="info"
                      fontWeight="medium"
                      textGradient
                      onClick={saveProfile}
                    >
                      Passer la configuration
                    </MDTypography>
                  </MDTypography>
                </MDBox>
              </MDBox>
            </>
          )}
          {step === 2 && (
            <>
              <MDBox
                mb={2}
                style={{ aspectRatio: 1, width: "100%", position: "relative", maxHeight: "50vh" }}
              >
                <div className="image-container">
                  {!upImg && (
                    <>
                      <ImageIcon style={{ width: 64, height: 64 }} />
                      <div>Sélectionner une image</div>
                      <input
                        className="image-uploader"
                        type="file"
                        accept="image/*"
                        onChange={onSelectFile}
                      />
                    </>
                  )}
                  {upImg && (
                    <ReactCrop
                      className={crop.isVertical ? "VerticalImage" : ""}
                      src={upImg}
                      crop={crop}
                      minWidth={128}
                      minHeight={128}
                      keepSelection
                      circularCrop
                      onChange={setCropWithImageDimension}
                      onImageLoaded={onImageLoad}
                    />
                  )}
                </div>
              </MDBox>
              <MDBox mt={4} mb={1} sx={{ display: "flex" }}>
                <MDButton
                  variant="gradient"
                  color="secondary"
                  onClick={handleBackStep2}
                  sx={{ flex: "1", px: 0 }}
                >
                  <ArrowBackIcon color={"inherit"} /> Retour
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="secondary"
                  onClick={clearImage}
                  disabled={!upImg}
                  sx={{ flex: "1", mx: 2, px: 0 }}
                >
                  Effacer l&apos;image
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleSubmitStep2}
                  sx={{ flex: "1", px: 0 }}
                >
                  Suivant <ArrowForwardIcon color={"inherit"} />
                </MDButton>
              </MDBox>
            </>
          )}
          {step === 3 && (
            <>
              <MDBox display="flex" alignItems="center" mb={0.5} ml={-1.5}>
                <MDBox mt={0.5}>
                  <Switch
                    checked={notifications}
                    disabled={notificationsDisabled}
                    onChange={() => setNotifications(!notifications)}
                  />
                </MDBox>
                <MDBox width="80%" ml={0.5}>
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    onClick={
                      notificationsDisabled ? undefined : () => setNotifications(!notifications)
                    }
                    sx={notificationsDisabled ? { opacity: 0.3 } : { cursor: "pointer" }}
                  >
                    <NotificationsIcon
                      color={"inherit"}
                      style={{ height: 24, width: 24, verticalAlign: "middle" }}
                    />
                    Recevoir des notifications sur cet appareil
                  </MDTypography>
                </MDBox>
              </MDBox>
              <MDBox display="flex" alignItems="center" mb={0.5} ml={-1.5}>
                <MDBox mt={0.5}>
                  <Switch
                    disabled={!userData.email}
                    checked={!!userData.email && emails}
                    onChange={() => setEmails(!emails)}
                  />
                </MDBox>
                <MDBox width="80%" ml={0.5}>
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    onClick={!userData.email ? undefined : () => setEmails(!emails)}
                    sx={!userData.email ? { opacity: 0.3 } : { cursor: "pointer" }}
                  >
                    <EmailIcon
                      color={"inherit"}
                      style={{ height: 24, width: 24, verticalAlign: "middle" }}
                    />
                    Recevoir des notifications par courriel
                  </MDTypography>
                </MDBox>
              </MDBox>
              <MDBox mt={4} mb={1} sx={{ display: "flex" }}>
                <MDButton
                  variant="gradient"
                  color="secondary"
                  onClick={handleBackStep3}
                  sx={{ flex: "1", marginRight: 2 }}
                >
                  <ArrowBackIcon color={"inherit"} /> Retour
                </MDButton>
                <MDButton variant="gradient" color="info" onClick={saveProfile} sx={{ flex: "1" }}>
                  Terminer&nbsp;
                  <CheckCircleIcon color={"inherit"} />
                </MDButton>
              </MDBox>
            </>
          )}
          {loading && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                paddingTop: "10%",
                zIndex: 1,
              }}
            >
              <CircularProgress color="info" />
              <MDTypography display="block" variant="button" color="info" fontWeight="medium" m={3}>
                {loading}
              </MDTypography>
            </div>
          )}
        </MDBox>
      </Card>
      <Snackbar
        open={error.open}
        autoHideDuration={5000}
        onClose={closeError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert variant="filled" severity="error">
          Une erreur est survenue lors de la sauvegarde des données.
          <br />
          Veuillez réessayer ultérieurement.
        </Alert>
      </Snackbar>
    </BasicLayout>
  );
}

SignUp.propTypes = {
  className: PropTypes.string,
};

export default styled(SignUp)(({ theme }) => {
  const { boxShadows } = theme;
  const { md, xl } = boxShadows;

  return {
    "& .image-container": {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: md,
      cursor: "pointer",

      "&:hover": {
        boxShadow: xl,
      },

      "& .image-uploader": {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0,
        cursor: "pointer",
      },

      "& .ReactCrop.VerticalImage": {
        height: "100%",

        "& > div": {
          height: "100%",
        },

        "& .ReactCrop__image": {
          maxHeight: "100%",
        },
      },
    },
  };
});
